import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { EASE } from '@/animations/variants';

const navLinks = [
  { label: 'メニュー', href: '#menu' },
  { label: 'コンセプト', href: '#about' },
  { label: '設備', href: '#facilities' },
  { label: 'アクセス', href: '#access' },
  { label: 'ご予約', href: '#booking' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const opaqueHeader = scrolled || mobileOpen;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        opaqueHeader ? 'bg-cream/95 backdrop-blur-sm border-b border-earth/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="flex flex-col">
              <span className={`font-serif-jp text-sm sm:text-base font-semibold transition-colors duration-500 ${opaqueHeader ? 'text-deep-brown' : 'text-cream'}`}>
                ほぐし屋さん
              </span>
              <span className={`font-serif-jp text-[10px] sm:text-xs tracking-wider transition-colors duration-500 ${opaqueHeader ? 'text-earth' : 'text-cream/80'}`}>
                Luminosite
              </span>
            </div>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className={`font-sans-jp text-sm tracking-wider cursor-pointer whitespace-nowrap transition-colors duration-300 hover:text-earth ${
                  opaqueHeader ? 'text-deep-brown' : 'text-cream/95'
                }`}
              >
                {link.label}
              </a>
            ))}
          </nav>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? '閉じる' : 'メニュー'}
            aria-expanded={mobileOpen}
          >
            <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl transition-colors duration-500 ${opaqueHeader ? 'text-deep-brown' : 'text-cream'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="md:hidden overflow-hidden bg-cream shadow-lg border-t border-earth/15"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
          >
            <nav className="flex flex-col px-6 py-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="group flex items-center justify-between font-sans-jp text-base text-deep-brown font-medium py-4 -mx-6 px-6 border-b border-earth/10 last:border-b-0 hover:bg-soft-green/40 hover:text-forest active:bg-soft-green/60 transition-colors duration-200"
                >
                  <span>{link.label}</span>
                  <i className="ri-arrow-right-s-line text-earth/60 text-lg transition-transform duration-200 group-hover:translate-x-1"></i>
                </a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="md:hidden fixed inset-0 top-16 sm:top-20 bg-deep-brown/30 z-[-1]"
            onClick={() => setMobileOpen(false)}
            aria-hidden
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: EASE }}
          />
        )}
      </AnimatePresence>
    </header>
  );
}